import { useEffect, useMemo, useRef } from "react";

import { QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary, ErrorFallback, usePusherChannel } from "@recruiter/shared";

import { basePusherConfig, sellerChannel } from "./config";
import { ProjectDirectory } from "./components/seller/project-directory";
import { DebugNotePanel } from "./components/seller/debug-note-panel";
import { DirectoryRail } from "./components/talent/directory-rail";
import { InsightsPanel } from "./components/talent/insights-panel";
import { PreferencesDrawer } from "./components/talent/preferences-drawer";
import { PreferencesSidebar } from "./components/talent/preferences-sidebar";
import { ProjectList } from "./components/talent/project-list";
import { ProjectPanel } from "./components/talent/project-panel";
import { TalentTopbar } from "./components/talent/talent-topbar";
import { queryClient } from "./lib/query-client";
import { pathToRoute, routeForState, routeToPath } from "./lib/route-url";
import { applySellerEvent, watchTalentSwitches } from "./lib/seller-stream-dispatcher";
import { captureViewport } from "./lib/debug-screenshot";
import { postDebugNote } from "./lib/seller-client";
import { useSellerStore } from "./lib/store/seller-store";
import { SELLER_EVENT_NAMES, streamEventKey, type SellerLiveEvent } from "./lib/seller-stream";

// The talent platform: one static bundle, its own origin, stub `x-seller-id` identity.
// A tester role-plays a talent Mira (STERLING) approached. With no talent picked it is the
// project directory beside its rail; with one it is the talent's PAGE, full width: a top
// bar (Pro insights, identity) over their preferences as they stand (the left column, with
// an edit button for the full drawer, which slides in from the left) beside their projects
// stacked as cards. No inbox until they open one: a card's "Open inbox" slides the project's
// conversation over the page from the right, and any press outside it closes it again. Pro
// insights open over the page.
//
// Data layer (mirrors web): server state lives in the TanStack Query cache
// (lib/queries/seller.ts); UI/session state in the Zustand store
// (lib/store/seller-store.ts); live Pusher events fold into the cache through
// applySellerEvent. No pulse refetch.
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SellerWorkspace />
    </QueryClientProvider>
  );
}

function SellerWorkspace() {
  const activeSellerId = useSellerStore((s) => s.activeSellerId);
  const selectedThreadId = useSellerStore((s) => s.selectedThreadId);
  const openSellerThread = useSellerStore((s) => s.openSellerThread);
  const clearActiveSeller = useSellerStore((s) => s.clearActiveSeller);
  const debugMode = useSellerStore((s) => s.debugMode);
  const setDebugMode = useSellerStore((s) => s.setDebugMode);
  const prefsOpen = useSellerStore((s) => s.prefsOpen);
  const insightsOpen = useSellerStore((s) => s.insightsOpen);

  // Debug-mode capture: record the tester's note in the admin "Debug Notes" tab
  // (the talent-side twin of the buyer app's `submitDebugNote`). Reads identity
  // from the store (not reactive) and tags the note with which view it came from,
  // captures a best-effort screenshot, then posts, throwing on failure so the
  // panel surfaces it inline. Never intercepts the STERLING reply composer.
  const submitDebugNote = async (message: string): Promise<void> => {
    const state = useSellerStore.getState();
    const stage = !state.activeSellerId
      ? "talent:directory"
      : state.selectedThreadId
        ? "talent:thread"
        : "talent:inbox";
    // Best-effort + bounded: a failure/timeout yields null and the note still saves.
    const screenshot = await captureViewport();
    const res = await postDebugNote({
      note: message,
      sellerId: state.activeSellerId,
      stage,
      screenshot,
    });
    if (!res.ok) throw new Error(res.message);
  };

  const config = useMemo(() => basePusherConfig(activeSellerId), [activeSellerId]);
  const channel = activeSellerId ? sellerChannel(activeSellerId) : null;
  const events = usePusherChannel<SellerLiveEvent>(channel, SELLER_EVENT_NAMES, config);

  // De-dupe redelivery + StrictMode double-mounts by (turn_id, seq); fold each NEW
  // event into the Query cache via the dispatcher (which reads activeSellerId from
  // the store to key its writes).
  const seen = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const ev of events) {
      const key = streamEventKey(ev);
      if (seen.current.has(key)) continue;
      seen.current.add(key);
      applySellerEvent(ev);
    }
  }, [events]);

  // New channel on identity switch -> forget the old channel's seen keys.
  useEffect(() => {
    seen.current = new Set();
  }, [activeSellerId]);

  // Pusher carries only the talent on screen, so whatever lands for one the tester switches away
  // from is never seen. Mark each talent's cached chats stale as it is left, so switching back
  // re-reads them once (a mark, not a poll: nothing re-reads while a talent stays open).
  useEffect(() => watchTalentSwitches(), []);

  // Keep the URL in sync with the store (deep links + back/forward, no router:
  // mirrors web's overlay sync in workspace-app.tsx). The pathname-equality
  // guard makes this a no-op when a popstate just drove the state change, so
  // push/pop never loop. The FIRST run replaces instead of pushing: boot
  // canonicalizes the URL ("/" with a stored identity -> "/s/<id>", unknown path
  // -> "/") without minting a history entry. `location.search` rides along so
  // `?debug=1` survives navigation.
  const bootSync = useRef(true);
  useEffect(() => {
    const desired = routeToPath(routeForState(activeSellerId, selectedThreadId));
    if (window.location.pathname !== desired) {
      const url = desired + window.location.search;
      if (bootSync.current) window.history.replaceState({}, "", url);
      else window.history.pushState({}, "", url);
    }
    bootSync.current = false;
  }, [activeSellerId, selectedThreadId]);

  // Back/Forward: parse the path and dispatch to the matching store action. The
  // three route kinds map 1:1 onto the three persisting actions, so localStorage
  // keeps mirroring the URL's identity. Unknown path -> directory (same fallback
  // as boot). Mounted once for the SPA lifetime.
  useEffect(() => {
    const onPop = () => {
      const route = pathToRoute(window.location.pathname) ?? { kind: "directory" as const };
      const store = useSellerStore.getState();
      if (route.kind === "directory") store.clearActiveSeller();
      else if (route.kind === "inbox") store.setActiveSeller(route.sellerId);
      else store.openSellerThread(route.sellerId, route.threadId);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return (
    <div
      className={`sl-shell${activeSellerId ? " sl-shell--talent" : ""}${
        debugMode ? " sl-shell--debug" : ""
      }`}
    >
      {debugMode ? (
        <div className="sl-debug-banner" role="status">
          <span className="sl-debug-banner__dot" aria-hidden="true" />
          <span className="sl-debug-banner__text">
            Debug mode is on. Use the note box to log what feels wrong on any page.
          </span>
          <button
            type="button"
            className="sl-debug-banner__off"
            onClick={() => setDebugMode(false)}
          >
            Turn off
          </button>
        </div>
      ) : null}
      {activeSellerId ? null : <DirectoryRail />}
      <main className="sl-main">
        {!activeSellerId ? (
          <ErrorBoundary
            resetKeys={[activeSellerId]}
            fallbackRender={({ resetErrorBoundary }) => (
              <ErrorFallback
                variant="section"
                title="The project directory hit a snag"
                message="Try again, or type a talent id in the sidebar."
                primaryAction={{ label: "Try again", onClick: resetErrorBoundary }}
              />
            )}
          >
            <ProjectDirectory onPickSeller={openSellerThread} />
          </ErrorBoundary>
        ) : (
          <div className="sl-talent">
            <TalentTopbar />
            <div className="sl-talent__body">
              <ErrorBoundary
                resetKeys={[activeSellerId]}
                fallbackRender={({ resetErrorBoundary }) => (
                  <aside className="sl-prefs-side">
                    <ErrorFallback
                      variant="inline"
                      title="Your preferences hit a snag"
                      message="Your projects are still available."
                      primaryAction={{ label: "Try again", onClick: resetErrorBoundary }}
                    />
                  </aside>
                )}
              >
                <PreferencesSidebar />
              </ErrorBoundary>
              <ErrorBoundary
                resetKeys={[activeSellerId]}
                fallbackRender={({ resetErrorBoundary }) => (
                  <ErrorFallback
                    variant="section"
                    title="Your projects hit a snag"
                    message="An open inbox is still available."
                    primaryAction={{ label: "Try again", onClick: resetErrorBoundary }}
                    secondaryAction={{ label: "Browse all projects", onClick: clearActiveSeller }}
                  />
                )}
              >
                <ProjectList />
              </ErrorBoundary>
            </div>
            {selectedThreadId ? (
              <ErrorBoundary
                resetKeys={[activeSellerId, selectedThreadId]}
                fallbackRender={({ resetErrorBoundary }) => (
                  <div className="sl-inbox">
                    <aside className="sl-panel">
                      <ErrorFallback
                        variant="inline"
                        title="This inbox hit a snag"
                        message="Your projects are still available."
                        primaryAction={{ label: "Try again", onClick: resetErrorBoundary }}
                        secondaryAction={{
                          label: "Close",
                          onClick: () => useSellerStore.getState().setSelectedThread(null),
                        }}
                      />
                    </aside>
                  </div>
                )}
              >
                <ProjectPanel threadId={selectedThreadId} />
              </ErrorBoundary>
            ) : null}
          </div>
        )}
      </main>
      {activeSellerId && prefsOpen ? (
        <ErrorBoundary
          resetKeys={[activeSellerId]}
          fallbackRender={({ resetErrorBoundary }) => (
            <div className="sl-drawer">
              <div className="sl-drawer__panel">
                <ErrorFallback
                  variant="inline"
                  title="Your preferences hit a snag"
                  message="Close this and try again."
                  primaryAction={{ label: "Try again", onClick: resetErrorBoundary }}
                />
              </div>
            </div>
          )}
        >
          <PreferencesDrawer />
        </ErrorBoundary>
      ) : null}
      {activeSellerId && insightsOpen ? (
        <ErrorBoundary
          resetKeys={[activeSellerId]}
          fallbackRender={({ resetErrorBoundary }) => (
            <div className="sl-insights">
              <div className="sl-insights__panel">
                <ErrorFallback
                  variant="inline"
                  title="Your insights hit a snag"
                  message="Close this and try again."
                  primaryAction={{ label: "Try again", onClick: resetErrorBoundary }}
                />
              </div>
            </div>
          )}
        >
          <InsightsPanel />
        </ErrorBoundary>
      ) : null}
      {debugMode ? (
        <DebugNotePanel onSubmit={submitDebugNote} onTurnOff={() => setDebugMode(false)} />
      ) : null}
    </div>
  );
}
